/* eslint-disable @typescript-eslint/no-explicit-any */
import invariant from 'invariant'
import { NO_OF_LETTERS_IN_SIGN_IN_CODE, ONE_SECOND } from './Consts.js'

export const groupBy = <T>(xs: Array<T>, key: keyof T): { [key: string]: Array<T> } => {
  return xs.reduce(
    (rv, x: any) => {
      // eslint-disable-next-line prettier/prettier
      ;(rv[x[key]] = rv[x[key]] || []).push(x)
      return rv
    },
    {} as { [key: string]: Array<T> }
  )
}

export const mean = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length

export const createUniqueId = () => Math.random().toString(36).substr(2, 9)

/** Deterministic 9-char base36 id (same shape as `createUniqueId`) derived from a seed string. Two
 * FNV-1a passes with different offsets give enough bits that the id space matches the random one. */
export const deterministicIdFromString = (seed: string): string => {
  const fnv1a = (text: string, offsetBasis: number): number => {
    let hash = offsetBasis >>> 0
    for (let index = 0; index < text.length; index++) {
      hash ^= text.charCodeAt(index)
      hash = Math.imul(hash, 16777619) >>> 0 // FNV prime
    }
    return hash >>> 0
  }
  const combined = fnv1a(seed, 2166136261).toString(36) + fnv1a(seed, 1831565813).toString(36)
  return combined.padEnd(9, '0').slice(0, 9)
}

/** Narrow an unknown catch value to an Error, so reporters and loggers always get a stack. */
export const toError = (value: unknown): Error => (value instanceof Error ? value : new Error(String(value)))

const FUNCTIONS_ERROR_CODE_PREFIX = 'functions/'

/** The code of an error a callable raised, without its `functions/` prefix; none for any other error. */
const readCallableErrorCode = (error: unknown): string | undefined => {
  const code = (error as { code?: unknown } | null | undefined)?.code
  const isCallableCode = typeof code === 'string' && code.startsWith(FUNCTIONS_ERROR_CODE_PREFIX)
  if (!isCallableCode) return undefined

  return code.slice(FUNCTIONS_ERROR_CODE_PREFIX.length)
}

/** An error a callable raised: the answer of the function itself, or the Firebase SDK's when none came back. */
export const isCallableError = (error: unknown): boolean => readCallableErrorCode(error) !== undefined

/** A callable whose answer never came back (a dropped connection, a phone that slept), so it may still have run. */
export function isCallableAnswerLost(error: unknown): boolean {
  const code = readCallableErrorCode(error)

  // With no answer to read, the Firebase SDK can only repeat the code as the message.
  return code !== undefined && (error as { message?: unknown }).message === code
}

export const createGuid = () => {
  const s4 = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1)

  return `${s4()}-${s4()}-${s4()}-${s4()}-${s4()}`
}

export const getArray = (length: number): Array<number> =>
  // eslint-disable-next-line prefer-spread
  Array.apply(null, { length } as any).map(Number.call, Number) as number[]

// Returns a new array with the elements randomly reordered (Fisher–Yates). Does not mutate input.
export const shuffle = <T>(arr: Array<T>): Array<T> => {
  const result = [...arr]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

export const asObject = <T>(arr: Array<T>, key: keyof T): { [key: string]: T } =>
  arr.reduce(
    (a, b) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      a[b[key]] = b
      return a
    },
    {} as { [key: string]: T }
  )

export const removeDuplicates = <T>(arr: Array<T>, key: keyof T): T[] => asArray(asObject(arr, key))

export function removeDuplicateStrings<T>(arr: T[] = []): T[] {
  return [...new Set(arr.filter((a) => !!a) || [])]
}

export const asArray = <T>(obj: { [key: string]: T }): T[] => Object.keys(obj).map((_id) => obj[_id])

export const isNumber = (n: unknown) => typeof n === 'number' && !isNaN(n)
export const isString = (n: unknown) => typeof n === 'string'

export const getRandomValue = (floor: number, ceil: number) => {
  const diff = ceil - floor
  return floor + Math.random() * diff
}

export const getMean = (arr: number[]) => {
  if (arr.length === 0) return 0
  return arr.reduce((a, b) => a + b, 0) / arr.length
}

export const formatPercent = (n: number) => `${n.toFixed()}%`

// True when the ISO time is at least `ageMs` before `nowMs`; a missing time counts as infinitely old.
export const isOlderThan = (isoTime: string | undefined, ageMs: number, nowMs: number): boolean =>
  nowMs - new Date(isoTime ?? 0).getTime() >= ageMs

const ISO_DATE_LENGTH = 10 // the YYYY-MM-DD of an ISO timestamp

/** The day of an ISO timestamp: '2026-06-05T10:18:00.000Z' becomes '2026-06-05'. */
export const toIsoDateOnly = (isoTime: string): string => isoTime.slice(0, ISO_DATE_LENGTH)

// Keeps only the finite numbers of a datapoint record; strings, nested objects and NaN are dropped.
export const extractNumericValues = (value: Record<string, unknown> | undefined | null): Record<string, number> => {
  if (!value) return {}

  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => typeof entry === 'number' && Number.isFinite(entry))
  ) as Record<string, number>
}

// Averages several records field by field: [{ a: 1, b: 4 }, { a: 3 }] becomes { a: 2, b: 4 }.
// A record that lacks a field is left out of that field's average rather than counted as 0.
export const aggregateMeanByKey = (snapshots: Record<string, number>[]): Record<string, number> => {
  const sums: Record<string, number> = {}
  const counts: Record<string, number> = {}

  for (const snapshot of snapshots) {
    for (const [key, value] of Object.entries(snapshot)) {
      sums[key] = (sums[key] ?? 0) + value
      counts[key] = (counts[key] ?? 0) + 1
    }
  }

  return Object.fromEntries(Object.keys(sums).map((key) => [key, sums[key] / (counts[key] ?? 1)]))
}

export const capitalize = (s: string) => {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const EMAIL_ADDRESS_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Something@something.something, with no spaces: the shape every address check uses. */
export const isEmailAddress = (value: string): boolean => EMAIL_ADDRESS_PATTERN.test(value)

export const getDomain = (email: string) => {
  const domain = email.split('@')[1]
  invariant(domain, 'Cant parse email: %s', email)
  return domain.toLowerCase()
}

export const getEmailForFirebaseUser = (user: any): string => {
  return user.email || user.providerData?.[0]?.email
}

export const mapCssHeightToNumber = (cssHeightInPixels?: string) =>
  (!!cssHeightInPixels && parseInt(cssHeightInPixels.replace('px', ''))) || 0

export function getDecimals(num: number) {
  if (Math.floor(num.valueOf()) === num.valueOf()) return 0
  return num.toString().split('.')[1]?.length || 0
}

export const toPx = (cssString: string) => parseInt(cssString.replace('px', ''))

export function getObjectOrBoolForJSON(str = ''): object | boolean {
  try {
    return JSON.parse(str)
  } catch (e) {
    return false
  }
}

export function getObjectForJSON(str = ''): object | undefined {
  try {
    return JSON.parse(str)
  } catch (e) {
    return
  }
}

export const getChunks = <T>(array: Array<T>, chunkSize: number): Array<Array<T>> => {
  let i
  let j
  let temparray = [] as T[]
  const res = [] as T[][]
  for (i = 0, j = array.length; i < j; i += chunkSize) {
    temparray = array.slice(i, i + chunkSize)
    res.push(temparray)
  }

  return res
}

export function debounce(func: (...args: any[]) => void, timeout = 300) {
  let timer: NodeJS.Timeout
  return (...args: any[]) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func(...args)
    }, timeout)
  }
}

export function getAllKeys(obj: any, opts: { ignoredKeys: string[] } = { ignoredKeys: [] }) {
  const { ignoredKeys = [] } = opts
  let keys: string[] = []

  // eslint-disable-next-line guard-for-in
  for (const key in obj) {
    if (ignoredKeys.includes(key)) return keys
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      keys.push(key)
      keys = keys.concat(getAllKeys(obj[key]))
    } else {
      keys.push(key)
    }
  }

  return keys
}

export const runInSequence = (funcs: Array<() => Promise<any>>) => {
  let result = Promise.resolve() as any
  return Promise.all(
    funcs.map((task) => {
      result = result.then(() => task())
      return result
    })
  )
}

type LoggableOptions = { ignoreKeys?: string[] }

// Recursively strips null values (and any ignoreKeys) so an object is safe/clean to log.
export function getLoggableObject(obj: unknown, opts: LoggableOptions = {}): unknown {
  const { ignoreKeys = [] } = opts
  if (typeof obj !== 'number' && !obj) {
    return undefined
  }

  if (typeof obj !== 'object') {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map((o) => getLoggableObject(o, opts))
  }

  return Object.entries(obj as Record<string, unknown>)
    .filter(([, v]) => v !== null)
    .filter(([k]) => !ignoreKeys.includes(k))
    .reduce<Record<string, unknown>>((acc, [k, v]) => ({ ...acc, [k]: getLoggableObject(v, opts) }), {})
}

export function delay(ms: number = ONE_SECOND) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Rejects when the promise has not settled in time, so a hung call can never hold its caller open. */
export function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  let timer: NodeJS.Timeout
  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    new Promise<T>((_, reject) => {
      timer = setTimeout(() => reject(new Error(`${label}-timeout-after-${timeoutMs}ms`)), timeoutMs)
    })
  ])
}

// True for the rejection `withTimeout` produces — the race gave up waiting; the underlying work was NOT cancelled.
export function isWithTimeoutErrorMessage(message: string): boolean {
  return /-timeout-after-\d+ms$/.test(message)
}

export function getRandomCode(): string {
  return getArray(NO_OF_LETTERS_IN_SIGN_IN_CODE)
    .map(() => Math.min(Math.round(Math.random() * 10), 9))
    .join('')
}

export function hasArrayDiff<T>(a: T[] = [], b: T[] = []): boolean {
  if (a.length !== b.length) return true
  return a.some((item) => !b.includes(item))
}

const FIVE_POINT_SCALE_MAX = 5

/** A 0–1 score as a whole score from 1 to 5, the way the star charts show it: 0.62 becomes 3. */
export const toFivePointScore = (normalizedScore: number): number =>
  Math.max(1, Math.min(FIVE_POINT_SCALE_MAX, Math.round(normalizedScore * FIVE_POINT_SCALE_MAX)))

/** Absorbs the float error of `score * rangeCount`: `0.6 * 5` is `2.9999…`, which floors into the range below. */
const RANGE_EDGE_TOLERANCE = 1e-9

/** Which of `rangeCount` equal ranges a 0–1 score falls in, from 0: with 5 ranges 0.6 is 3 and 1 is 4. */
export const toRangeIndex = (score: number, rangeCount: number): number =>
  Math.min(rangeCount - 1, Math.max(0, Math.floor(score * rangeCount + RANGE_EDGE_TOLERANCE)))

/** What was added or removed between two lists: `['a', 'b']` then `['b', 'c']` gives `['a', 'c']`. */
export const findAddedOrRemoved = <T>(before: T[] = [], after: T[] = []): T[] => [
  ...before.filter((item) => !after.includes(item)),
  ...after.filter((item) => !before.includes(item))
]

export const truncateNumericValues = <T>(value: T, precision = 2): T => {
  if (Array.isArray(value)) {
    return value.map((item) => truncateNumericValues(item, precision)) as unknown as T
  }

  if (value && typeof value === 'object') {
    if (value instanceof Date) {
      return value
    }

    const prototype = Object.getPrototypeOf(value)
    if (prototype !== Object.prototype && prototype !== null) {
      return value
    }

    return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, val]) => {
      acc[key] = truncateNumericValues(val, precision)
      return acc
    }, {}) as unknown as T
  }

  if (typeof value === 'number') {
    // Convert NaN/Infinity to null for JSON serialization
    if (!Number.isFinite(value)) {
      return null as unknown as T
    }

    if (Number.isInteger(value)) {
      return value
    }

    const factor = 10 ** precision
    const truncated = Math.trunc(value * factor)
    return (truncated / factor) as unknown as T
  }

  return value
}

export const toNonNegativeInt = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0

export const truncate = (value: string, max: number): string =>
  value.length <= max ? value : `${value.slice(0, max)}…`

/** Collapses every run of whitespace (incl. newlines) into single spaces and trims. */
export const collapseWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim()

/** Text made safe to place in HTML, in an element or a quoted attribute: & < > " ' become entities. */
export const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

/** `value` without `marker`, including a marker formed where the pieces around a removed one meet. */
export const removeEveryOccurrence = (value: string, marker: string): string => {
  if (!marker) return value

  let remaining = value
  while (remaining.includes(marker)) remaining = remaining.split(marker).join('')
  return remaining
}

/** The value at a dotted path of nested objects, `{ a: { b: 1 } }` at `a.b` is 1; undefined where a step is missing. */
export const readNestedValue = (source: unknown, dottedPath: string): unknown =>
  dottedPath
    .split('.')
    .reduce<unknown>(
      (node, part) => (typeof node === 'object' && node !== null ? (node as Record<string, unknown>)[part] : undefined),
      source
    )

/** Marks stored copy that still awaits translation; the apps show the text behind it. */
export const TRANSLATE_MARKER = '$PLEASE_TRANSLATE$'

/** The object without the keys that hold `undefined`, for a write that must not carry them. */
export const omitUndefined = <Value extends object>(value: Value): Value =>
  Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as Value

/** The ids this app writes (`createUniqueId`, Firestore's own): letters, digits, `-` and `_`, 128 at most, never `__x__`. */
const DOCUMENT_ID_PATTERN = /^(?!__.*__$)[A-Za-z0-9_-]{1,128}$/

/** A usable Firestore document id; anything else would make Firestore throw instead of being refused. */
export const isUsableDocumentId = (value: unknown): value is string =>
  typeof value === 'string' && DOCUMENT_ID_PATTERN.test(value)

/** No id at all (a callable sends a missing one as `null`), or a usable document id. */
export const isOptionalDocumentId = (value: unknown): boolean =>
  value === undefined || value === null || isUsableDocumentId(value)

/**
 * Removes markdown emphasis/heading marks, leaving the words. For surfaces that render model output as
 * plain text: a reply written for speech and read back in a chat bubble otherwise shows its `**stars**`.
 * Deliberately narrow — it strips decoration, it does not parse markdown.
 */
export const stripMarkdownFormatting = (value: string): string =>
  value
    .replace(/(\*\*\*|___)(\S(?:[\s\S]*?\S)?)\1/g, '$2')
    .replace(/(\*\*|__)(\S(?:[\s\S]*?\S)?)\1/g, '$2')
    .replace(/(?<![\w*])\*(\S(?:[^*\n]*?\S)?)\*(?![\w*])/g, '$1')
    .replace(/(?<![\w_])_(\S(?:[^_\n]*?\S)?)_(?![\w_])/g, '$1')
    .replace(/`([^`\n]+)`/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')

/** First sentence of a text, without trailing whitespace; '' when there is none. */
export const getFirstSentence = (value: string): string => value.match(/^[^.!?]+[.!?]?/)?.[0]?.trim() || ''

/** Lowercases only the first character, for splicing a sentence into the middle of another one. */
export const lowerCaseFirstLetter = (value: string): string => (value ? value[0].toLowerCase() + value.slice(1) : value)

/** Joins the non-empty values as lines, each collapsed to a single line. */
export const joinNonEmptyLines = (values: string[]): string => values.map(collapseWhitespace).filter(Boolean).join('\n')

/** True when any of the values contains the search text, ignoring case; an empty search matches everything. */
export const matchesSearchText = (values: Array<string | undefined | null>, searchText: string): boolean => {
  const needle = searchText.trim().toLowerCase()
  if (!needle) return true

  return values.some((value) => (value ?? '').toLowerCase().includes(needle))
}

export type TtlCache<T> = {
  get: (key: string, now?: number) => T | undefined
  set: (key: string, value: T, now?: number) => void
  size: () => number
  clear: () => void
}

/**
 * In-memory value cache with a TTL and a hard entry cap, for warm server instances.
 *
 * A plain Map that only evicts when the same key is re-read grows without bound on a long-lived
 * instance: every distinct key leaks until the process restarts. Writes here sweep expired entries
 * first, then drop the oldest inserted keys once `maxEntries` is exceeded (insertion-ordered Map).
 * Per-instance only: with several warm instances a cached value is a best-effort saving, never a
 * correctness guarantee, so callers must work identically on a miss.
 */
export const createTtlCache = <T>(ttlMs: number, maxEntries = 500): TtlCache<T> => {
  const entries = new Map<string, { expiresAt: number; value: T }>()

  const sweepExpired = (now: number) => {
    entries.forEach((entry, key) => {
      if (entry.expiresAt <= now) entries.delete(key)
    })
  }

  return {
    get: (key, now = Date.now()) => {
      const entry = entries.get(key)
      if (!entry) return undefined
      if (entry.expiresAt <= now) {
        entries.delete(key)
        return undefined
      }
      return entry.value
    },
    set: (key, value, now = Date.now()) => {
      entries.delete(key)
      entries.set(key, { expiresAt: now + ttlMs, value })
      if (entries.size > maxEntries) {
        sweepExpired(now)
        while (entries.size > maxEntries) {
          const oldestKey = entries.keys().next().value
          if (oldestKey === undefined) break
          entries.delete(oldestKey)
        }
      }
    },
    size: () => entries.size,
    clear: () => entries.clear()
  }
}

export type TtlDedupe = {
  // True when this key was already seen inside the window; records it otherwise.
  isDuplicate: (key: string, now?: number) => boolean
  clear: () => void
}

// In-memory "have I seen this key recently" guard. Prunes lazily once maxTrackedKeys is
// exceeded so a long-lived process cannot grow the map without bound.
export const createTtlDedupe = (windowMs: number, maxTrackedKeys = 1000): TtlDedupe => {
  const seenAt = new Map<string, number>()

  return {
    isDuplicate: (key: string, now = Date.now()) => {
      const lastSeenAt = seenAt.get(key)
      if (lastSeenAt !== undefined && now - lastSeenAt < windowMs) return true

      if (seenAt.size > maxTrackedKeys) {
        seenAt.forEach((at, existingKey) => {
          if (now - at >= windowMs) seenAt.delete(existingKey)
        })
      }
      seenAt.set(key, now)
      return false
    },
    clear: () => seenAt.clear()
  }
}

// Normalizes date-ish strings and Firestore Timestamps (admin or client — anything with toDate())
// to an ISO string; undefined when missing or unparseable.
export const toIsoString = (value?: string | { toDate: () => Date } | null): string | undefined => {
  if (!value) return undefined
  const date = typeof value === 'string' ? new Date(value) : value.toDate()
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

// Compact date-time for tables (sv-SE gives ISO-like `YYYY-MM-DD HH:mm`); '—' for missing/invalid.
export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// ============================================================================
// Draft-js WYSIWYG Format
// ============================================================================

export interface DraftJsBlock {
  key: string
  text: string
  type: string
  depth: number
  inlineStyleRanges: unknown[]
  entityRanges: unknown[]
  data: Record<string, unknown>
}

export interface DraftJsContent {
  blocks: DraftJsBlock[]
  entityMap: Record<string, unknown>
}

function parseBoldRanges(text: string): {
  text: string
  inlineStyleRanges: Array<{ offset: number; length: number; style: string }>
} {
  // Converts **bold** markers into DraftJS inlineStyleRanges.
  // Keeps everything else as-is.
  const ranges: Array<{ offset: number; length: number; style: string }> = []
  let out = ''

  let i = 0
  while (i < text.length) {
    const start = text.indexOf('**', i)
    if (start === -1) {
      out += text.slice(i)
      break
    }

    const end = text.indexOf('**', start + 2)
    if (end === -1) {
      out += text.slice(i)
      break
    }

    out += text.slice(i, start)
    const boldText = text.slice(start + 2, end)
    const offset = out.length
    out += boldText
    if (boldText.length) {
      ranges.push({ offset, length: boldText.length, style: 'BOLD' })
    }
    i = end + 2
  }

  return { text: out, inlineStyleRanges: ranges }
}

function blockTypeForLine(line: string): { type: string; text: string } {
  const trimmed = line.trimStart()

  // Headings
  if (trimmed.startsWith('### ')) return { type: 'header-three', text: trimmed.slice(4) }
  if (trimmed.startsWith('## ')) return { type: 'header-two', text: trimmed.slice(3) }
  if (trimmed.startsWith('# ')) return { type: 'header-one', text: trimmed.slice(2) }

  // Bullets
  const unordered = /^[-*•]\s+/.exec(trimmed)
  if (unordered) return { type: 'unordered-list-item', text: trimmed.replace(/^[-*•]\s+/, '') }

  const ordered = /^\d+\.\s+/.exec(trimmed)
  if (ordered) return { type: 'ordered-list-item', text: trimmed.replace(/^\d+\.\s+/, '') }

  return { type: 'unstyled', text: line }
}

/**
 * Wraps a plain text string in Draft-js format for WYSIWYG editor compatibility.
 * This is used to convert backend-generated text (like LLM analysis) into
 * a format that the frontend draft-js editor can consume directly.
 */
export function wrapInDraft(text: string): DraftJsContent {
  const safe = typeof text === 'string' ? text : String(text ?? '')
  const lines = safe.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n')

  const blocks: DraftJsBlock[] = lines.map((line, index) => {
    // Preserve blank lines as paragraph breaks
    if (!line.trim()) {
      return {
        key: `b${index}`,
        text: '',
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {}
      }
    }

    const { type, text: rawText } = blockTypeForLine(line)
    const boldParsed = parseBoldRanges(rawText)

    return {
      key: `b${index}`,
      text: boldParsed.text,
      type,
      depth: 0,
      inlineStyleRanges: boldParsed.inlineStyleRanges,
      entityRanges: [],
      data: {}
    }
  })

  return { blocks, entityMap: {} }
}
